import { Box, Typography } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

/** 룰렛 참여율 차트 한 행: dayLabel + 컬럼별 비율(%) */
export type RouletteTabChartDatum = {
  dayLabel: string;
  [key: string]: number | string;
};

const LINE_COLORS = [
  '#ec4899', // 1룰렛(럭키룰렛)
  '#60a5fa', // 3룰렛
  '#22c55e', // 5룰렛
  '#f59e0b', // 10룰렛
  '#8b5cf6', // 15룰렛
  '#06b6d4', // 30룰렛
  '#94a3b8', // 50룰렛
  '#f97316', // 70룰렛
];

type Props = {
  data?: RouletteTabChartDatum[];
};

export default function RouletteContentRouletteTabChart({ data }: Props) {
  const chartData = data ?? [];
  const keys = chartData.length
    ? (Object.keys(chartData[0]).filter((k) => k !== 'dayLabel') as string[])
    : [];

  return (
    <Box
      sx={{
        border: '1px solid #e5e7eb',
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        p: 2,
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight={600}
        textAlign="center"
        sx={{ mb: 2, color: '#475569', fontSize: '0.9rem' }}
      >
        룰렛 참여율 현황
      </Typography>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" vertical={false} />
          <XAxis dataKey="dayLabel" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              return (
                <Box
                  sx={{
                    p: 1.5,
                    minWidth: 160,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    {label}
                  </Typography>
                  {payload.map((entry) => (
                    <Typography key={entry.dataKey} variant="body2" sx={{ mb: 0.25 }}>
                      {entry.name}:{' '}
                      {typeof entry.value === 'number' ? `${entry.value}%` : entry.value}
                    </Typography>
                  ))}
                </Box>
              );
            }}
          />
          <Legend />
          {keys.map((key, i) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={LINE_COLORS[i % LINE_COLORS.length]}
              strokeWidth={2}
              dot={{ r: 4 }}
              name={key}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
